import { useState, useEffect } from 'react';
import { Check, ChevronDown, Building2, Search } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useActiveBrand } from '@/contexts/BrandContext';
import { BrandLogo } from '@/components/BrandLogo';
import { cn } from '@/lib/utils';

/**
 * Global brand switcher — lives in the app top bar.
 * - Popover combobox listing every brand in the current agency scope.
 * - Selecting a brand updates BrandContext (URL + localStorage), no navigation.
 * - "All brands" resets scope to null.
 * - Cmd/Ctrl+B opens the popover from anywhere.
 */
export function BrandSwitcher() {
  const { brand, brands, setBrand } = useActiveBrand();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'b') {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, []);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            'inline-flex items-center gap-2 h-8 pl-1.5 pr-2 rounded-md',
            'text-[12.5px] font-medium text-white/85',
            'hover:bg-white/10 transition-colors border border-transparent hover:border-white/10 shrink-0 max-w-[220px]',
          )}
          aria-label="Switch brand"
        >
          {brand ? (
            <BrandLogo name={brand.name} logoUrl={brand.logoUrl} size={20} rounded="md" />
          ) : (
            <span className="h-5 w-5 rounded-md bg-white/10 flex items-center justify-center">
              <Building2 className="h-3 w-3 text-white/70" />
            </span>
          )}
          <span className="truncate">{brand ? brand.name : 'All brands'}</span>
          <ChevronDown className="h-3 w-3 opacity-60 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="p-0 w-72" align="start" sideOffset={6}>
        <Command>
          <div className="flex items-center border-b px-3">
            <Search className="h-3.5 w-3.5 text-muted-foreground mr-2" />
            <CommandInput placeholder="Switch brand…" className="h-9 border-0 focus:ring-0" />
          </div>
          <CommandList>
            <CommandEmpty>No brands found.</CommandEmpty>
            <CommandGroup heading="Scope">
              <CommandItem
                value="__all"
                onSelect={() => { setBrand(null); setOpen(false); }}
                className="gap-2"
              >
                <span className="h-6 w-6 rounded-md bg-muted flex items-center justify-center">
                  <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                </span>
                <span className="flex-1">All brands</span>
                {!brand && <Check className="h-3.5 w-3.5" />}
              </CommandItem>
            </CommandGroup>
            <CommandGroup heading="Brands">
              {brands.map((b) => (
                <CommandItem
                  key={b.id}
                  value={b.name}
                  onSelect={() => { setBrand(b.id); setOpen(false); }}
                  className="gap-2"
                >
                  <BrandLogo name={b.name} logoUrl={b.logoUrl} size={22} rounded="md" />
                  <span className="flex-1 truncate">{b.name}</span>
                  {brand?.id === b.id && <Check className="h-3.5 w-3.5" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          <div className="border-t px-3 py-2 text-[10px] text-muted-foreground flex items-center justify-between">
            <span>Switch brand</span>
            <kbd className="px-1.5 py-0.5 rounded bg-muted font-mono text-[10px]">⌘B</kbd>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
